import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChatSocketOptional } from "../../context/ChatSocketContext";
import { useAuth } from "../../context/AuthContext";

/**
 * Live-Präsenz der Kundenakte: wer sie gerade offen hat und wer in welchem Feld
 * arbeitet. Andockpunkt ist der Socket.IO-Raum `contactview:<id>` — ein EIGENER
 * Raum neben dem des Kundentagebuchs (`useKundentagebuchLive`): `contact:leave`
 * ist socket-weit und würde dem Tagebuch sonst die Zustellung abreißen.
 *
 * Die Frames tragen nur `{ userId, displayName, fieldKey }`. Ein Feldinhalt
 * verlässt den Client NIE — ungespeicherte Kundendaten bleiben hier.
 *
 * KEIN Lock: Das Speichern schreibt das ganze Formular (last write wins). Die
 * Markierung ist ein Hinweis, keine Sperre — das Feld bleibt für jeden bedienbar.
 *
 * Alle Wege sind selbstheilend: Weder ein Reconnect noch ein gedrosselter Tab
 * (Browser takten `setInterval` in verborgenen Tabs auf ~1/Minute) darf den
 * eigenen Eintrag oder die eigene Feld-Markierung dauerhaft verlieren lassen.
 */

export interface KontaktBetrachter {
  userId: string;
  displayName: string;
}

/** Lebenszeichen des Betrachters; der Server antwortet mit `forbidden`, wenn der Raum weg ist. */
const VIEW_PING_MS = 15_000;
/** Puls für das gerade fokussierte Feld; die Markierung läuft nach 15 s ab. */
const FIELD_HEARTBEAT_MS = 5_000;

export function useContactPresence(contactId: number | null | undefined): {
  connected: boolean;
  /** Andere Nutzer, die die Akte offen haben (ohne einen selbst). */
  betrachter: KontaktBetrachter[];
  /** Anzeigename des Kollegen, der gerade in diesem Feld arbeitet. */
  bearbeitetVon: (fieldKey: string) => string | undefined;
  feldFokus: (fieldKey: string) => void;
  feldBlur: (fieldKey: string) => void;
} {
  const socket = useChatSocketOptional();
  const connected = socket?.connected ?? false;
  const { user } = useAuth();
  const currentUserId = (user?.id ?? null) as string | null;

  const [alleBetrachter, setAlleBetrachter] = useState<KontaktBetrachter[]>([]);
  const [felder, setFelder] = useState<Map<string, KontaktBetrachter>>(() => new Map());
  /**
   * Feld, in dem der Cursor dieses Tabs steht. Überlebt bewusst den Effekt-
   * Cleanup: nach Reconnect/Kontaktwechsel muss die Markierung neu gesetzt
   * werden, sonst bliebe das Feld unmarkiert, bis der Nutzer es verlässt und
   * neu anklickt. Geleert wird der Ref nur durch echtes Blur.
   */
  const eigenesFeld = useRef<string | null>(null);
  /**
   * Hält der Server die Markierung gerade für uns? Nur dann wird gepulst —
   * hält sie ein Kollege, schweigt dieser Tab, statt im Takt anzuklopfen.
   */
  const fokusGewaehrt = useRef(false);
  /** Vom Effekt gesetzt, damit ein `forbidden` von außerhalb einen Neu-Beitritt auslösen kann. */
  const beitreten = useRef<(() => void) | null>(null);

  /**
   * Fokus melden und die Antwort auswerten. Der Ack ist wichtig: ohne ihn
   * verschwände ein `forbidden` (Raumzustand verloren) stumm, und die Feld-
   * Präsenz dieses Tabs wäre bis zum nächsten Kontaktwechsel tot.
   */
  const fokusSenden = useCallback(
    (fieldKey: string) => {
      if (!socket || !connected || !contactId || !fieldKey) return;
      void socket
        .emitAck<{ granted?: boolean }>("contact:field:focus", { contactId, fieldKey })
        .then((res) => {
          if (eigenesFeld.current === fieldKey) fokusGewaehrt.current = res.granted !== false;
        })
        .catch(() => {
          if (eigenesFeld.current === fieldKey) fokusGewaehrt.current = false;
          // `forbidden` heißt: dieser Socket sitzt nicht (mehr) im Präsenz-Raum.
          // Neu beitreten holt Betrachterliste und Markierung zurück.
          beitreten.current?.();
        });
    },
    [socket, connected, contactId],
  );

  // Der Effekt hängt bewusst an `connected`: der Präsenz-Zustand lebt im
  // Arbeitsspeicher des Backends. Nach einem Deploy/Neustart ist er weg — ohne
  // erneuten Join nach dem Reconnect bliebe die Betrachterliste dauerhaft leer.
  useEffect(() => {
    if (!socket || !connected || !contactId || !Number.isFinite(contactId)) return;
    let aktiv = true;

    // Ein abgelehnter Join (kein Leserecht) wird still verworfen — die Akte
    // funktioniert dann ohne Präsenzanzeige weiter.
    const joinSenden = () => {
      void socket
        .emitAck<{
          viewers?: KontaktBetrachter[];
          fields?: Array<{ fieldKey: string; userId: string; displayName: string }>;
        }>("contact:view:join", { contactId })
        .then((res) => {
          if (!aktiv) return;
          setAlleBetrachter(res.viewers ?? []);
          setFelder(
            new Map(
              (res.fields ?? []).map((f) => [
                f.fieldKey,
                { userId: f.userId, displayName: f.displayName },
              ]),
            ),
          );
          // Sitzt der Cursor noch im Feld (Reconnect, Kontaktwechsel), muss die
          // Markierung nach dem Re-Join erneut gesetzt werden.
          if (eigenesFeld.current) fokusSenden(eigenesFeld.current);
        })
        .catch(() => undefined);
    };
    beitreten.current = joinSenden;
    joinSenden();

    const offViewers = socket.on(
      "contact:viewers",
      (p: { contactId?: number; viewers?: KontaktBetrachter[] }) => {
        if (!p || p.contactId !== contactId) return;
        setAlleBetrachter(Array.isArray(p.viewers) ? p.viewers : []);
      },
    );

    const offField = socket.on(
      "contact:field",
      (p: {
        contactId?: number;
        fieldKey?: string;
        userId?: string | null;
        displayName?: string | null;
      }) => {
        if (!p || p.contactId !== contactId || !p.fieldKey) return;
        const fieldKey = p.fieldKey;
        setFelder((prev) => {
          const next = new Map(prev);
          if (p.userId) next.set(fieldKey, { userId: p.userId, displayName: p.displayName || "" });
          else next.delete(fieldKey);
          return next;
        });
        // Gibt der Kollege unser Feld frei, holen wir die Markierung nach —
        // ohne diesen Weg bliebe sie aus, solange der Cursor stehen bleibt.
        if (!p.userId && eigenesFeld.current === fieldKey && !fokusGewaehrt.current) {
          fokusSenden(fieldKey);
        }
      },
    );

    const ping = setInterval(() => {
      void socket
        .emitAck("contact:view:ping", { contactId })
        .catch(() => beitreten.current?.());
    }, VIEW_PING_MS);

    const puls = setInterval(() => {
      const fieldKey = eigenesFeld.current;
      if (!fieldKey || !fokusGewaehrt.current) return;
      void socket
        .emitAck<{ granted?: boolean }>("contact:field:heartbeat", { contactId, fieldKey })
        .then((res) => {
          if (!aktiv || eigenesFeld.current !== fieldKey || res.granted !== false) return;
          // Die Markierung ist abgelaufen (gedrosselter Tab) — erneut setzen,
          // statt sie stumm verwaisen zu lassen.
          fokusGewaehrt.current = false;
          fokusSenden(fieldKey);
        })
        .catch(() => undefined);
    }, FIELD_HEARTBEAT_MS);

    return () => {
      aktiv = false;
      beitreten.current = null;
      clearInterval(ping);
      clearInterval(puls);
      offViewers();
      offField();
      // Die Markierung serverseitig freigeben — `eigenesFeld` bleibt aber
      // stehen, damit ein Re-Join sie wieder setzt (siehe oben).
      if (eigenesFeld.current) {
        socket.emit("contact:field:blur", { contactId, fieldKey: eigenesFeld.current });
        fokusGewaehrt.current = false;
      }
      socket.emit("contact:view:leave", { contactId });
      setAlleBetrachter([]);
      setFelder(new Map());
    };
  }, [socket, connected, contactId, currentUserId, fokusSenden]);

  const feldFokus = useCallback(
    (fieldKey: string) => {
      if (!socket || !connected || !contactId || !fieldKey) return;
      // Ein Wechsel ohne Blur (Tab-Taste, Klick ins nächste Feld) darf das alte
      // Feld nicht markiert zurücklassen.
      if (eigenesFeld.current && eigenesFeld.current !== fieldKey) {
        socket.emit("contact:field:blur", { contactId, fieldKey: eigenesFeld.current });
      }
      eigenesFeld.current = fieldKey;
      fokusGewaehrt.current = false;
      fokusSenden(fieldKey);
    },
    [socket, connected, contactId, fokusSenden],
  );

  const feldBlur = useCallback(
    (fieldKey: string) => {
      if (!socket || !contactId || !fieldKey) return;
      if (eigenesFeld.current === fieldKey) {
        eigenesFeld.current = null;
        fokusGewaehrt.current = false;
      }
      socket.emit("contact:field:blur", { contactId, fieldKey });
    },
    [socket, contactId],
  );

  const betrachter = useMemo(
    () => alleBetrachter.filter((b) => b.userId !== currentUserId),
    [alleBetrachter, currentUserId],
  );

  const bearbeitetVon = useCallback(
    (fieldKey: string) => {
      const halter = felder.get(fieldKey);
      if (!halter || halter.userId === currentUserId) return undefined;
      return halter.displayName || "Ein Kollege";
    },
    [felder, currentUserId],
  );

  return { connected, betrachter, bearbeitetVon, feldFokus, feldBlur };
}
